import { useState } from 'react';
import { filterOptions } from '@/mocks/content';

export interface FilterState {
  region: string;
  year: string;
  genre: string;
  sort: string;
}

interface FilterBarProps {
  state: FilterState;
  onChange: (key: keyof FilterState, value: string) => void;
  onReset: () => void;
}

function FilterRow({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: string[];
  value: string;
  onSelect: (option: string) => void;
}) {
  return (
    <div className="flex items-start gap-3 md:gap-4">
      <span className="w-12 md:w-14 shrink-0 pt-1 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = option === value;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onSelect(option)}
              className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                active
                  ? 'bg-primary-500 text-background-50 font-medium'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({ state, onChange, onReset }: FilterBarProps) {
  const [panelOpen, setPanelOpen] = useState(false);

  const activeCount = [state.region, state.year, state.genre].filter(
    (value) => value !== '全部',
  ).length;

  const rows = (
    <div className="space-y-4">
      <FilterRow
        label="地区"
        options={filterOptions.regions}
        value={state.region}
        onSelect={(value) => onChange('region', value)}
      />
      <FilterRow
        label="年代"
        options={filterOptions.years}
        value={state.year}
        onSelect={(value) => onChange('year', value)}
      />
      <FilterRow
        label="类型"
        options={filterOptions.genres}
        value={state.genre}
        onSelect={(value) => onChange('genre', value)}
      />
      <FilterRow
        label="排序"
        options={filterOptions.sorts}
        value={state.sort}
        onSelect={(value) => onChange('sort', value)}
      />
    </div>
  );

  return (
    <>
      <div className="hidden md:block rounded-lg bg-background-50 border border-background-200 p-5">
        {rows}
      </div>

      <div className="md:hidden">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg bg-background-50 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <i className="ri-equalizer-line text-lg" />
            筛选
            {activeCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-accent-500 text-background-50 text-[11px] flex items-center justify-center">
                {activeCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg bg-background-50 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer"
          >
            <i className="ri-sort-desc text-lg" />
            {state.sort}
          </button>
          <button
            type="button"
            onClick={onReset}
            className="w-11 h-11 rounded-lg bg-background-50 border border-background-200 flex items-center justify-center text-foreground-700 cursor-pointer"
            aria-label="重置筛选"
          >
            <i className="ri-refresh-line text-lg" />
          </button>
        </div>

        {panelOpen && (
          <div className="fixed inset-0 z-[60]">
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setPanelOpen(false)}
              className="absolute inset-0 bg-primary-950/50 cursor-pointer"
            />
            <div className="absolute inset-x-0 bottom-0 bg-background-50 rounded-t-2xl max-h-[80vh] overflow-y-auto p-5">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading font-semibold text-lg text-foreground-950">
                  筛选与排序
                </h3>
                <button
                  type="button"
                  onClick={() => setPanelOpen(false)}
                  className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xl" />
                </button>
              </div>
              {rows}
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    onReset();
                    setPanelOpen(false);
                  }}
                  className="flex-1 h-11 rounded-md bg-secondary-100 text-secondary-900 text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  重置
                </button>
                <button
                  type="button"
                  onClick={() => setPanelOpen(false)}
                  className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  查看结果
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}